import { Component, computed, inject, input, output } from '@angular/core';

import { FilterCountBadgeDirective } from '@app/shared/directives/filter-count-badge.directive';
import { AbsenceReportDTO } from '@app/shared/services/dtos/reports/absence-report.dto';
import { TimeReportPanelFiltersService } from '@app/shared/services/time-report-panel-filters.service';
import {
  GroupingModeEnum,
  ReportColumnsEnum,
  TimeReportFilterName,
  TimeReportFilters
} from '@app/shared/types/models/reports/time-report-panel.model';
import { WorkTimeReport } from '@app/shared/types/models/reports/work-time-report';
import { UiSelectComponent } from '@app/shared/ui';
import { UiButtonComponent } from '@app/shared/ui/button/ui-button.component';

@Component({
  selector: 'proffeo-time-report-panel-filters',
  templateUrl: './time-report-panel-filters.component.html',
  styleUrls: ['./time-report-panel-filters.component.scss'],
  imports: [UiButtonComponent, FilterCountBadgeDirective, UiSelectComponent]
})
export class TimeReportPanelFiltersComponent {
  private readonly filtersService = inject(TimeReportPanelFiltersService);

  protected readonly GroupingModeEnum = GroupingModeEnum;

  protected filters = this.filtersService.filtersState;
  protected filterConfigs = computed(() =>
    this.filtersService.filterConfigs().filter(filter => !this.isAbsenceReports() || !filter.hideForAbsence)
  );

  public isAbsenceReports = input<boolean>(false);
  public selectedTimeReportIds = input<string[]>([]);
  public selectedGroupingMode = input<GroupingModeEnum>(GroupingModeEnum.NONE);
  public sessionsToggled = input<boolean>(false);
  public filteredTimeReports = input<WorkTimeReport[][] | AbsenceReportDTO[][]>([]);
  public flattenedTimeReports = input<WorkTimeReport[] | AbsenceReportDTO[]>([]);
  public visibleTableColumns = input<ReportColumnsEnum[]>([]);

  public filtersChanged = output<TimeReportFilters>();
  public changeGroupingMode = output<GroupingModeEnum>();
  public expandAll = output<void>();
  public hideAll = output<void>();
  public addReport = output<void>();
  public approveReports = output<void>();

  protected getFilterValue(filterName: TimeReportFilterName): string | string[] {
    if (filterName === TimeReportFilterName.Kind) {
      return this.filters().kind ?? 'All';
    }
    if (filterName === TimeReportFilterName.Status) {
      return this.filters().status ?? 'All';
    }
    return this.filtersService.getSelectedValues(filterName);
  }

  protected getFilterBadgeCount(filterName: TimeReportFilterName): number {
    if (filterName === TimeReportFilterName.Kind) {
      return this.filters().kind && this.filters().kind !== 'All' ? 1 : 0;
    }
    if (filterName === TimeReportFilterName.Status) {
      return this.filters().status && this.filters().status !== 'All' ? 1 : 0;
    }
    const value = this.getFilterValue(filterName);
    return Array.isArray(value) ? value.length : value && value !== 'All' ? 1 : 0;
  }

  protected onFilterValueChange(filterName: TimeReportFilterName, value: string | string[] | null): void {
    const normalized = Array.isArray(value) ? value : value == null ? [] : [value];
    this.updateFilter(filterName, normalized);
  }

  protected updateFilter(filter: TimeReportFilterName, value: string[]): void {
    this.filtersService.updateFilter(filter, value);
    this.filtersService.applyFilters();
    this.filtersChanged.emit(this.filtersService.appliedFilters());
  }

  protected clearFilters(): void {
    this.filtersService.resetFilters();
    this.filtersChanged.emit(this.filtersService.appliedFilters());
  }
}
